/**
 * RISK CARD COMPONENT
 * 
 * Displays a single risk entry with expandable details
 * Shows category, impact, description, mitigation, and contingency
 * 
 * Based on Document 5 specification
 */

import React, { useState } from 'react';
import { Edit2, Trash2, ChevronDown, ChevronRight, MapPin, DollarSign, Clock, Users } from 'lucide-react';
import { getRiskCategory, getImpactLevel, RISK_OWNERS } from '../../constants/riskCategories';

const RiskCard = ({
  risk,
  onEdit,
  onDelete,
  isLocked = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const category = getRiskCategory(risk.category);
  const impact = getImpactLevel(risk.impact_level);
  const owner = RISK_OWNERS.find(o => o.value === risk.risk_owner);

  const getImpactColorClasses = () => {
    switch (risk.impact_level) {
      case 'high':
        return {
          border: 'border-red-500/40',
          bg: 'bg-red-500/5',
          badge: 'bg-red-500/20 text-red-400',
        };
      case 'medium':
        return {
          border: 'border-amber-500/40',
          bg: 'bg-amber-500/5',
          badge: 'bg-amber-500/20 text-amber-400',
        };
      case 'low':
      default:
        return {
          border: 'border-emerald-500/40',
          bg: 'bg-emerald-500/5',
          badge: 'bg-emerald-500/20 text-emerald-400',
        };
    }
  };

  const colors = getImpactColorClasses();

  return (
    <div className={`border rounded overflow-hidden ${colors.border} ${colors.bg}`}>
      {/* Header - always visible */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="text-2xl flex-shrink-0">{category.icon}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-200">{category.label}</span>
              <span className={`px-2 py-0.5 text-xs font-medium rounded ${colors.badge}`}>
                {impact.label}
              </span>
            </div>
            <p className="text-sm text-slate-400 truncate mt-0.5">
              {risk.description}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 ml-3 flex-shrink-0">
          {risk.area_label && (
            <span className="hidden sm:flex items-center gap-1 text-xs text-slate-500">
              <MapPin className="w-3 h-3" />
              {risk.area_label}
            </span>
          )}
          
          {!isLocked && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit?.();
                }}
                className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded transition-colors"
                title="Edit"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirm('Delete this risk?')) {
                    onDelete?.();
                  }
                }}
                className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          )}
          
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-slate-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-slate-500" />
          )}
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-2 border-t border-slate-700/50 space-y-4">
          {/* Full Description */}
          <div>
            <span className="text-xs text-slate-500 uppercase tracking-wider">Description</span>
            <p className="text-slate-300 mt-1">{risk.description}</p>
          </div>

          {/* Impact Details Row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-2 bg-slate-800/50 rounded">
              <span className="text-xs text-slate-500 block">Impact</span>
              <span className={`text-sm font-medium ${colors.badge.split(' ')[1]}`}>
                {impact.icon} {impact.label}
              </span>
            </div>
            
            {risk.cost_impact_estimate && (
              <div className="p-2 bg-slate-800/50 rounded">
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <DollarSign className="w-3 h-3" /> Est. Cost
                </span>
                <span className="text-sm text-slate-300">{risk.cost_impact_estimate}</span>
              </div>
            )}
            
            {risk.time_impact_estimate && (
              <div className="p-2 bg-slate-800/50 rounded">
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Est. Time
                </span>
                <span className="text-sm text-slate-300">{risk.time_impact_estimate}</span>
              </div>
            )}
            
            {owner && (
              <div className="p-2 bg-slate-800/50 rounded">
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <Users className="w-3 h-3" /> Owner
                </span>
                <span className="text-sm text-slate-300">{owner.label}</span>
              </div>
            )}
          </div>

          {/* Area Link */}
          {risk.area_label && (
            <div className="sm:hidden">
              <span className="text-xs text-slate-500">Related Area</span>
              <p className="text-slate-300 text-sm mt-0.5 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {risk.area_label}
              </p>
            </div>
          )}

          {/* Mitigation Plan */}
          {risk.mitigation_plan && (
            <div>
              <span className="text-xs text-slate-500 uppercase tracking-wider">Mitigation Plan</span>
              <p className="text-slate-300 mt-1">{risk.mitigation_plan}</p>
            </div>
          )}

          {/* Contingency */}
          {risk.contingency_included && risk.contingency_amount && (
            <div className="flex items-center gap-2 p-2 bg-emerald-500/10 border border-emerald-500/30 rounded">
              <span className="text-emerald-400">✓</span>
              <span className="text-slate-300 text-sm">
                Contingency included: 
                <span className="font-semibold text-emerald-400 ml-1">
                  ${parseFloat(risk.contingency_amount).toLocaleString()}
                </span>
              </span>
            </div>
          )}

          {/* Created timestamp */}
          {risk.created_at && (
            <div className="text-xs text-slate-600 pt-2 border-t border-slate-700/50">
              Added {new Date(risk.created_at).toLocaleString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RiskCard;
